import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { addSchemaToPage, removePageSchemas, getBreadcrumbSchema, getWebPageSchema } from '../../utils/seo';

const EmpresaPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [aboutRef, aboutInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [processRef, processInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    document.title = 'Empresa - Instaladores de Pladur en Badalona | PLADUREX';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Conoce PLADUREX, empresa especializada en instalación de pladur en Badalona. Profesionales con experiencia, materiales de calidad y trabajo limpio.');
    }

    // Add Schema.org JSON-LD
    const schemas = [
      addSchemaToPage(getBreadcrumbSchema([
        { name: 'Inicio', url: '/' },
        { name: 'Empresa', url: '/empresa' }
      ])),
      addSchemaToPage(getWebPageSchema(
        'Empresa - PLADUREX',
        'Conoce PLADUREX, empresa especializada en instalación de pladur en Badalona. Profesionales con experiencia, materiales de calidad y trabajo limpio.',
        '/empresa'
      ))
    ];

    return () => {
      removePageSchemas();
    };
  }, []);

  const workSteps = [
    {
      icon: 'ri-ruler-line',
      title: 'Visita y medición',
      description: 'Nos desplazamos a tu ubicación en Badalona para evaluar el proyecto, tomar medidas exactas y entender tus necesidades específicas.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Propuesta detallada',
      description: 'Elaboramos un presupuesto claro con todos los detalles: materiales, plazos, acabados y precio final sin sorpresas.',
    },
    {
      icon: 'ri-tools-line',
      title: 'Ejecución profesional',
      description: 'Realizamos la instalación con precisión, respetando los plazos acordados y manteniendo tu espacio limpio y ordenado.',
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: 'Remates y entrega',
      description: 'Finalizamos con acabados perfectos, listos para pintar. Limpieza completa y revisión final antes de la entrega.',
    },
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Materiales de calidad',
      description: 'Trabajamos exclusivamente con placas de pladur certificadas y perfilería de primera calidad para garantizar durabilidad y seguridad.',
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Trabajo en viviendas ocupadas',
      description: 'Coordinamos horarios flexibles y trabajamos con orden y limpieza para que puedas seguir con tu rutina sin grandes molestias.',
    },
    {
      icon: 'ri-team-line',
      title: 'Coordinación y comunicación',
      description: 'Mantenemos comunicación constante durante todo el proyecto. Resolvemos dudas y ajustamos detalles sobre la marcha.',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Facturación y garantías',
      description: 'Emitimos factura por todos nuestros trabajos y ofrecemos garantía sobre la instalación realizada.',
    },
  ];

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section ref={heroRef} className="relative pt-36 md:pt-32 pb-20 px-6 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/7c585775e991130d0a8382386547811b.jpg"
              alt="Equipo PLADUREX"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-dark-bg"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block px-4 py-2 bg-orange-primary/10 border border-orange-primary rounded-full mb-6"
            >
              <span className="text-orange-primary text-sm font-bold">Sobre nosotros</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-text-primary mb-6"
            >
              Instaladores profesionales de pladur en Badalona
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed"
            >
              Especialistas en construcción en seco con experiencia en todo tipo de proyectos residenciales y comerciales
            </motion.p>
          </div>
        </section>

        {/* About Section */}
        <section ref={aboutRef} className="py-24 px-6 bg-dark-card">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={aboutInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-extrabold text-text-primary mb-6">
                  Quiénes somos
                </h2>
                <div className="space-y-4 text-text-secondary text-base leading-relaxed">
                  <p>
                    <strong className="text-text-primary">PLADUREX</strong> es una empresa especializada en la instalación de pladur en Badalona y Barcelona. Ofrecemos soluciones completas para reformas, redistribuciones y mejoras en viviendas, oficinas y locales comerciales.
                  </p>
                  <p>
                    Nuestro equipo cuenta con amplia experiencia en todo tipo de instalaciones: desde <Link to="/servicios#tabiques" className="text-orange-primary hover:underline">tabiques divisorios</Link> y <Link to="/servicios#trasdosados" className="text-orange-primary hover:underline">trasdosados</Link> hasta <Link to="/servicios#techos" className="text-orange-primary hover:underline">falsos techos registrables</Link> y sistemas de <Link to="/servicios#aislamiento-acustico" className="text-orange-primary hover:underline">aislamiento acústico</Link> y <Link to="/servicios#aislamiento-termico" className="text-orange-primary hover:underline">térmico</Link>.
                  </p>
                  <p>
                    Trabajamos con <strong className="text-text-primary">materiales de primera calidad</strong>, respetamos los plazos acordados y mantenemos una comunicación fluida con nuestros clientes durante todo el proceso.
                  </p>
                  <p>
                    Ya sea que necesites dividir un espacio, mejorar el aislamiento de tu vivienda o realizar una reforma integral, en PLADUREX encontrarás la solución profesional que buscas. <Link to="/servicios" className="text-orange-primary hover:underline">Descubre todos nuestros servicios</Link>.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={aboutInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=craftsman%20installing%20drywall%20panels%20with%20precision%20in%20bright%20modern%20interior%20space&width=800&height=1000&seq=empresa-about-001&orientation=portrait"
                    alt="Instalación profesional de pladur"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/50 to-transparent"></div>
                </div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-orange-primary rounded-2xl opacity-20 blur-3xl"></div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How We Work Section */}
        <section ref={processRef} className="py-24 px-6 bg-dark-bg">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={processInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="inline-block px-4 py-2 bg-orange-primary/10 border border-orange-primary rounded-full mb-6"
              >
                <span className="text-orange-primary text-sm font-bold">Metodología</span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={processInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-4xl md:text-5xl font-extrabold text-text-primary mb-6"
              >
                Cómo trabajamos
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={processInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-text-secondary text-lg max-w-2xl mx-auto"
              >
                Un proceso ordenado y transparente desde el primer contacto hasta la entrega final
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {workSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={processInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-dark-card border border-dark-border rounded-xl p-8 hover:border-orange-primary transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-primary to-orange-secondary rounded-xl flex items-center justify-center mb-6">
                    <i className={`${step.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-text-primary mb-4">
                    {step.title}
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section ref={valuesRef} className="py-24 px-6 bg-dark-card">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="inline-block px-4 py-2 bg-orange-primary/10 border border-orange-primary rounded-full mb-6"
              >
                <span className="text-orange-primary text-sm font-bold">Nuestros valores</span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-4xl md:text-5xl font-extrabold text-text-primary mb-6"
              >
                Calidad y profesionalidad
              </motion.h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-dark-bg border border-dark-border rounded-xl p-8"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${value.icon} text-orange-primary text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-text-primary mb-3">
                        {value.title}
                      </h3>
                      <p className="text-text-muted text-[15px] leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 bg-dark-bg">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-extrabold text-text-primary mb-6"
            >
              ¿Hablamos de tu proyecto?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-text-secondary text-lg mb-10"
            >
              <Link to="/contacto" className="text-orange-primary hover:underline">Contacta con nosotros</Link> y te daremos un presupuesto personalizado sin compromiso
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contacto"
                className="px-8 py-4 bg-orange-primary text-white rounded-full hover:shadow-glow-orange transition-all duration-300 font-bold text-lg whitespace-nowrap inline-flex items-center justify-center space-x-2"
              >
                <i className="ri-mail-line"></i>
                <span>Solicitar presupuesto</span>
              </Link>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border-2 border-orange-primary text-orange-primary rounded-full hover:bg-orange-primary hover:text-white transition-all duration-300 font-bold text-lg whitespace-nowrap inline-flex items-center justify-center space-x-2"
              >
                <i className="ri-whatsapp-line"></i>
                <span>WhatsApp</span>
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-text-muted text-sm mt-8"
            >
              ¿Tienes dudas? Consulta nuestras <Link to="/faq" className="text-orange-primary hover:underline">preguntas frecuentes</Link>
            </motion.p>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default EmpresaPage;
